import dayjs from "dayjs";
import * as Yup from "yup";

const phoneRegExp = /^\d{10}$/; // Match exactly 10 digits (0-9)
const faxRegExp = /^\d{10}$/; // Match exactly 10 digits (0-9)
const mobileRegExp = /^\d{10}$/; // Match exactly 10 digits (0-9)

export const pharmacyDetailsValidationSchema = (validateFunction: any, selfPharmacyId: number = -1) => Yup.object().shape({
  legalName: Yup.string().required('Required'),
  ncpdp: Yup.string()
    .required('Required')
    .test('Unique NCPDP','NCPDP already exist',
    function(value) {return new Promise((resolve) => {
      validateFunction({ variables: { where: { ncpdp: { equals: value } } }}).then((res: any) => {
        if (res.data.findFirstPharmacy) {
          if (selfPharmacyId === res.data.findFirstPharmacy.id) {
            resolve(true);
          } else {
            resolve(false);
          }
        } else {
          resolve(true);
        }
      })
    })}
  ),
  npi: Yup.string().required('Required'),
  deaNumber: Yup.string()
    .required('Required')
    .test('Unique Dea number','Dea Number already exist',
      function(value){return new Promise((resolve) => {
        validateFunction({ variables: { where: { deaNumber: { equals: value } } }}).then((res: any) => {
          if (res.data.findFirstPharmacy) {
            if (selfPharmacyId === res.data.findFirstPharmacy.id) {
              resolve(true);
            } else {
              resolve(false);
            }
          } else {
            resolve(true);
          }
        })
      })}
    ),
  verifyDeaNumber: Yup.string()
    .required('Required')
    .oneOf([Yup.ref('deaNumber')], 'Your DEA numbers do not match.'),
  stateLicense: Yup.string().required('Required'),
  federalTaxId: Yup.string()
    .required('Required')
    .matches(/^[0-9]+$/, 'Must be only digits')
    .length(9, 'Must be 9 digits'),
  softwareName: Yup.string().required('Required'),
  email: Yup.string().required('Required').email('Email is invalid'),
  phone: Yup.string()
    .required('Required')
    .matches(phoneRegExp, 'Invalid phone number format'),
  mobile: Yup.string()
    .required('Required')
    .matches(mobileRegExp, 'Invalid mobile number format'),
  fax: Yup.string()
    .required('Required')
    .matches(faxRegExp, 'Invalid fax number format'),
  store: Yup.number().required('Required').typeError('Store must be a number'),
});

export const physicalAddressValidationSchema = Yup.object().shape({
  physicalAddress: Yup.string()
    .required('Required'),
  city: Yup.string()
    .required('Required'),
  state: Yup.string()
    .required('Required'),
  zipCode: Yup.string()
    .required('Required')
    .matches(/^[0-9]+$/, "Must be only digits")
    .length(5, 'Must be 5 digits')
});

export const bankAccountValidationSchema = Yup.object().shape({
  accountHolderName: Yup.string()
    .required('Required'),
  routingNumber: Yup.string()
    .required('Required')
    .matches(/^[0-9]+$/, "Must be only digits")
    .length(9, 'Must be 9 digits'),
  accountNumber: Yup.string()
    .required('Required')
    .matches(/^[0-9]+$/, "Must be only digits")
    .min(5, 'Account Number is too short - should be 5 chars minimum.')
    .max(17, 'Account Number is too long - should be 17 chars maximum.'),
  confirmAccountNumber: Yup.string()
    .required('Required')
    .oneOf([Yup.ref('accountNumber')], 'Your account numbers do not match.'),
});

export const otherDetailsValidationSchema = Yup.object().shape({
  managerContactName: Yup.string()
    .required('Required'),
  contractManager: Yup.string()
    .required('Required'),
  pharmacyType: Yup.string()
    .required('Required'),
});

export const updatePharmacyValidationSchema = Yup.object().shape({
  status: Yup.string()
    .required('Required'),
  deaLicenseDate: Yup.string()
    .required('Required'),
  ncpdpLicenseDate: Yup.string()
    .required('Required'),
});

const sellItemValidationSchema = (expirationMessage: string) => Yup.object().shape({
  ndc: Yup.string().required('Required'),
  lotNumber: Yup.string().required('Required'),
  drugName: Yup.string().required('Required'),
  labelerName: Yup.string().required('Required'),
  form: Yup.string().required('Required'),
  strength: Yup.string().required('Required'),
  quantityPerPack: Yup.number()
    .required('Required')
    .typeError('Quantity must be a number'),
  expDate: Yup.mixed()
    .test('required', 'Required', (value) => value != null && value !== '')
    .test(
      'valid-exp-date',
      'Enter a valid expiration date',
      (value) => {
        if (value == null || value === '') return true;
        return dayjs(value as dayjs.ConfigType).isValid();
      },
    )
    .test(
      'next-month-or-later',
      expirationMessage,
      (value) => {
        if (value == null || value === '') return true;
        const date = dayjs(value as dayjs.ConfigType);
        if (!date.isValid()) return true;
        return !date.startOf('month').isBefore(dayjs().add(1, 'month').startOf('month'));
      },
    ),
  sealed: Yup.string().when('type', {
    is: 'FULL',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.optional(),
  }),
  packageConditionType: Yup.string().required('Package condition is required'),
  partialQuantity: Yup.string().when('type', {
    is: 'PARTIAL',
    then: (schema) => schema.required('Partial quantity is required when packaging type is Partial'),
    otherwise: (schema) => schema.optional(),
  }),
  price: Yup.string()
  .required("Required")
  .matches(/^\d+(\.\d{1,2})?$/, "Price must be a valid number") // Ensures valid number format
});

export const postNewItemValidationSchema = sellItemValidationSchema(
  'Expiration date must be at least next month to post this listing.',
);

export const editSellItemValidationSchema = sellItemValidationSchema(
  'Expiration date must be at least next month to activate this listing.',
);

export const addUserValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .required('First name Required'),
  lastName: Yup.string()
    .required('Last name Required'),
  email: Yup.string()
    .required('Email is Required')
    .email('Email is invalid'),
  phone: Yup.string()
    .required('Phone number Required')
    .matches(phoneRegExp, 'Phone number is not valid')
});

export const editUserValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .required('First name Required'),
  lastName: Yup.string()
    .required('Last name Required'),
  phone: Yup.string()
    .required('Phone number Required')
    .matches(phoneRegExp, 'Phone number is not valid')
});

